import { useEffect, useRef, useCallback, useState } from 'react';
import { registerPlugin } from '@capacitor/core';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const WS_URL = API_URL.replace('https://', 'wss://').replace('http://', 'ws://');
const RECONNECT_DELAY = 5000; // 5 seconds
const PING_INTERVAL = 30000; // 30 seconds
const POSITION_SEND_INTERVAL = 10000; // Send position every 10 seconds via WebSocket
const HTTP_POLL_INTERVAL = 30000; // Send position via HTTP every 30 seconds as fallback
// distanceFilter=0 → CoreLocation délivre les updates en continu même à l'arrêt
// (gigue GPS naturelle ~1-3s). Nécessaire pour garder le statut "temps réel"
// backend quand le chauffeur est stationnaire écran verrouillé. Le plugin
// convertit 0 en kCLDistanceFilterNone (voir Plugin.swift ligne 109-111).
const NATIVE_DISTANCE_FILTER = 0;
const IS_NATIVE = typeof window !== 'undefined' && window.__IS_NATIVE__ === true;

// IMPORTANT : registerPlugin retourne un Proxy JS. Ne JAMAIS le renvoyer depuis
// une fonction async / await dessus — JS le confond avec un thenable et appelle
// .then(...) dessus, ce qui déclenche l'erreur Capacitor
// "BackgroundGeolocation.then() is not implemented on ios".
// On l'initialise en synchrone, une seule fois, au chargement du module.
const BackgroundGeolocation = IS_NATIVE ? registerPlugin('BackgroundGeolocation') : null;

// Plugin custom Android pour desactiver l'optimisation batterie (Doze mode)
// sur Pixel/Samsung. Sur iOS ce plugin n'est pas necessaire (foreground service natif).
const BatteryOptimization = IS_NATIVE ? registerPlugin('BatteryOptimization') : null;

// Plugin custom Android GPS natif (style Life360) - envoi HTTP direct depuis
// un foreground service Java, bypass complet la WebView. Fait fonctionner
// le tracking meme quand l'ecran est verrouille / app en background prolonge.
// Sur iOS on utilise deja SCLS via AppDelegate.swift.
const PropilotGps = IS_NATIVE ? registerPlugin('PropilotGps') : null;

// Detection Android (les paths natifs different iOS vs Android)
const IS_ANDROID = IS_NATIVE && typeof navigator !== 'undefined'
  && /android/i.test(navigator.userAgent || '');

// Convert native BG-Geolocation payload to browser-like Position for existing handlers
function nativeToPosition(location) {
  return {
    coords: {
      latitude: Number(location.latitude),
      longitude: Number(location.longitude),
      accuracy: location.accuracy == null ? null : Number(location.accuracy),
      altitude: location.altitude == null ? null : Number(location.altitude),
      speed: location.speed == null ? null : Number(location.speed), // m/s (like navigator)
      heading: location.bearing == null ? null : Number(location.bearing),
    },
    timestamp: Number(location.time || Date.now()),
  };
}

export const useLocationTracking = (options = {}) => {
  const { enabled = true } = options;
  const { token, isAuthenticated } = useAuth();
  const [isTracking, setIsTracking] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [lastPosition, setLastPosition] = useState(null);
  const [error, setError] = useState(null);
  
  const wsRef = useRef(null);
  const watchIdRef = useRef(null);
  const pingIntervalRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const lastSentRef = useRef(null);
  const positionQueueRef = useRef(null);
  const httpPollIntervalRef = useRef(null);
  const lastPositionRef = useRef(null); // Keep a ref for the HTTP poll

  // Send position via HTTP (fallback for background mode)
  const sendPositionViaHTTP = useCallback(async (position) => {
    if (!token) return;
    
    const lat = position?.coords?.latitude || position?.latitude;
    const lng = position?.coords?.longitude || position?.longitude;
    
    if (!lat || !lng) return;

    // Timestamp réel de la capture GPS (pas l'heure d'envoi).
    // Critique pour que la carte admin reste chronologiquement cohérente
    // même si les positions sont envoyées en batch après une zone offline.
    const captureTs = position?.timestamp
      ? new Date(position.timestamp).toISOString()
      : (position?.coords?.timestamp
          ? new Date(position.coords.timestamp).toISOString()
          : new Date().toISOString());

    try {
      const response = await fetch(`${API_URL}/api/gps/poll`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          lat: lat,
          lng: lng,
          speed: position?.coords?.speed ? position.coords.speed * 3.6 : (position?.speed || null),
          heading: position?.coords?.heading || position?.heading || null,
          accuracy: position?.coords?.accuracy || position?.accuracy || null,
          timestamp: captureTs
        })
      });
      
      if (response.ok) {
        console.log('[GPS HTTP] Position envoyée via HTTP polling');
      }
    } catch (err) {
      console.error('[GPS HTTP] Erreur envoi HTTP:', err);
    }
  }, [token]);

  // Connect to WebSocket
  const connectWebSocket = useCallback(() => {
    if (!token || wsRef.current?.readyState === WebSocket.OPEN) return;

    try {
      const wsUrl = `${WS_URL}/api/ws/driver?token=${token}`;
      console.log('[GPS WebSocket] Connexion en cours...');
      
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('[GPS WebSocket] Connecté');
        setIsConnected(true);
        setError(null);
        
        // Start ping interval to keep connection alive
        pingIntervalRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'ping' }));
          }
        }, PING_INTERVAL);
        
        // Send queued position if any
        if (positionQueueRef.current) {
          sendPositionViaWS(positionQueueRef.current);
          positionQueueRef.current = null;
        }
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          if (data.type === 'connected') {
            console.log('[GPS WebSocket] Confirmation de connexion reçue');
          } else if (data.type === 'ack') {
            console.log('[GPS WebSocket] Position confirmée par le serveur');
          } else if (data.type === 'pong') {
            // Heartbeat response - connection is alive
          }
        } catch (err) {
          console.error('[GPS WebSocket] Erreur parsing message:', err);
        }
      };

      ws.onerror = (event) => {
        console.error('[GPS WebSocket] Erreur:', event);
        setError('Erreur de connexion WebSocket');
      };

      ws.onclose = (event) => {
        console.log('[GPS WebSocket] Déconnecté, code:', event.code);
        setIsConnected(false);
        
        // Clear ping interval
        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
          pingIntervalRef.current = null;
        }
        
        // Attempt to reconnect if still tracking
        if (isTracking && event.code !== 4001 && event.code !== 4002) {
          console.log('[GPS WebSocket] Tentative de reconnexion dans', RECONNECT_DELAY/1000, 'secondes...');
          reconnectTimeoutRef.current = setTimeout(() => {
            connectWebSocket();
          }, RECONNECT_DELAY);
        }
      };
    } catch (err) {
      console.error('[GPS WebSocket] Erreur création connexion:', err);
      setError('Impossible de créer la connexion WebSocket');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, isTracking]);

  // Send position via WebSocket
  const sendPositionViaWS = useCallback((position) => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      // Queue position for when connection is established
      positionQueueRef.current = position;
      return false;
    }

    const locationData = {
      type: 'location',
      latitude: position.coords.latitude,
      longitude: position.coords.longitude,
      speed: position.coords.speed ? position.coords.speed * 3.6 : null, // Convert m/s to km/h
      heading: position.coords.heading,
      accuracy: position.coords.accuracy,
      altitude: position.coords.altitude,
      timestamp: new Date().toISOString()
    };

    try {
      wsRef.current.send(JSON.stringify(locationData));
      lastSentRef.current = Date.now();
      console.log('[GPS WebSocket] Position envoyée:', locationData.latitude.toFixed(4), locationData.longitude.toFixed(4));
      return true;
    } catch (err) {
      console.error('[GPS WebSocket] Erreur envoi position:', err);
      return false;
    }
  }, []);

  // Handle position update from geolocation API
  const handlePosition = useCallback((position) => {
    const now = Date.now();
    
    const posData = {
      latitude: position.coords.latitude,
      longitude: position.coords.longitude,
      speed: position.coords.speed ? position.coords.speed * 3.6 : 0,
      heading: position.coords.heading,
      accuracy: position.coords.accuracy,
      timestamp: new Date().toISOString()
    };
    
    // Update local state
    setLastPosition(posData);
    lastPositionRef.current = position;

    // Send via WebSocket at interval
    if (!lastSentRef.current || (now - lastSentRef.current) >= POSITION_SEND_INTERVAL) {
      const wsSent = sendPositionViaWS(position);
      // If WebSocket failed, send via HTTP immediately
      if (!wsSent) {
        sendPositionViaHTTP(position);
      }
    }
  }, [sendPositionViaWS, sendPositionViaHTTP]);

  // Handle geolocation error
  const handleError = useCallback((err) => {
    console.error('[GPS] Erreur géolocalisation:', err.message);
    setError(err.message);
  }, []);

  // Start HTTP polling as fallback (runs regardless of WebSocket status)
  const startHttpPolling = useCallback(() => {
    // Clear existing interval
    if (httpPollIntervalRef.current) {
      clearInterval(httpPollIntervalRef.current);
    }
    
    // Envoi HTTP en heartbeat : tous les 30s, MEME si WebSocket est OK
    // Ca garantit qu'au moins une position atteint le serveur toutes les 30s
    // (evite les chauffeurs qui passent en "hors ligne" a cause d'un WS silencieux)
    httpPollIntervalRef.current = setInterval(() => {
      if (lastPositionRef.current) {
        sendPositionViaHTTP(lastPositionRef.current);
      } else {
        // Pas de position en cache : essaie d'en recuperer une fraiche
        navigator.geolocation?.getCurrentPosition(
          (pos) => {
            lastPositionRef.current = pos;
            sendPositionViaHTTP(pos);
          },
          () => {}, // Silently fail
          { enableHighAccuracy: false, timeout: 10000, maximumAge: 30000 }
        );
      }
    }, HTTP_POLL_INTERVAL);
  }, [sendPositionViaHTTP]);

  // Start tracking
  const startTracking = useCallback(async () => {
    // Native mode (Capacitor iOS/Android): use @capacitor-community/background-geolocation
    if (IS_NATIVE) {
      try {
        if (!BackgroundGeolocation) throw new Error('Plugin BackgroundGeolocation indisponible');

        // Persister token + URL + flag pour que AppDelegate.swift (SCLS)
        // puisse envoyer la position au backend meme apres swipe-quit
        try {
          const { Preferences } = await import('@capacitor/preferences');
          const token = localStorage.getItem('token') || '';
          await Preferences.set({ key: 'propilote_auth_token', value: token });
          await Preferences.set({ key: 'propilote_backend_url', value: API_URL });
          await Preferences.set({ key: 'propilote_gps_enabled', value: 'true' });
          console.log('[GPS Native] Preferences persistees pour SCLS');
        } catch (prefErr) {
          console.warn('[GPS Native] Impossible de persister preferences SCLS:', prefErr);
        }

        console.log('[GPS Native] Démarrage du tracking en arrière-plan...');

        // ANDROID : permissions demandées seulement APRÈS l'écran de divulgation accepté
        if (IS_ANDROID && PropilotGps) {
          const fg = await PropilotGps.requestForegroundLocation();
          if (!fg?.location) {
            setError('Permission de localisation refusée');
            return false;
          }
          try {
            await PropilotGps.requestBackgroundLocation();
          } catch (bgErr) {
            console.warn('[GPS Android] Permission arrière-plan non accordée:', bgErr);
          }
        }

        // ANDROID: verifier l'exemption d'optimisation batterie AVANT de demarrer le watcher
        // Sans ca, sur Pixel/Samsung le foreground service est tue apres 2-5 min.
        if (IS_ANDROID && BatteryOptimization) {
          try {
            const { value: isIgnoring } = await BatteryOptimization.isIgnoringBatteryOptimizations();
            console.log('[GPS Android] isIgnoringBatteryOptimizations:', isIgnoring);
            if (!isIgnoring) {
              // Verifier si on a deja demande dans cette installation (evite spam)
              const alreadyAsked = localStorage.getItem('propilote_battery_opt_asked');
              if (!alreadyAsked) {
                console.log('[GPS Android] Ouverture prompt exemption batterie...');
                await BatteryOptimization.requestIgnoreBatteryOptimizations();
                localStorage.setItem('propilote_battery_opt_asked', 'true');
              } else {
                console.log('[GPS Android] Prompt deja affiche, tracking demarre quand meme');
              }
            }
          } catch (batteryErr) {
            console.warn('[GPS Android] Erreur check battery optimization:', batteryErr);
            // Non bloquant, on continue
          }
        }

        // ANDROID: demarrer le service GPS natif Java (bypass WebView)
        // C'est ce qui garantit que les positions continuent de partir meme quand
        // l'ecran est verrouille et que la WebView est mise en pause par Android.
        // Le service lit token + backend depuis Preferences (persistes ci-dessus)
        // et POST directement en OkHttp vers /api/gps/poll.
        let androidNativeGpsRunning = false;
        if (IS_ANDROID && PropilotGps) {
          try {
            await PropilotGps.start();
            androidNativeGpsRunning = true;
            console.log('[GPS Android] PropilotGps native service demarre — watcher WebView desactive');
          } catch (nativeSvcErr) {
            console.warn('[GPS Android] Impossible de demarrer PropilotGps:', nativeSvcErr);
            // Non bloquant, on tombe sur le plugin community
          }
        }

        // Si le service natif Android tourne, on NE demarre PAS le watcher WebView.
        // Sinon on aurait 2 systemes GPS en parallele → chaque fix GPS (1-2s) declenche
        // setState + WebSocket + HTTP dans la WebView, ce qui sature le thread JS et
        // fait geler l'app, echouer les scans, ralentir la navigation.
        // Le service natif POST directement au backend, on n'a rien a faire cote JS.
        if (androidNativeGpsRunning) {
          watchIdRef.current = { native: true, id: 'android-native-service' };
          setIsTracking(true);
          setError(null);

          // On demarre quand meme le WebSocket pour recevoir les commandes
          // en temps reel (nouvelle commande, etc.), mais SANS envoyer de GPS
          // depuis la WebView (le service natif s'en charge).
          connectWebSocket();

          console.log('[GPS Android] Mode natif pur active - aucun watcher WebView');
          return true;
        }

        const watcherId = await BackgroundGeolocation.addWatcher(
          {
            requestPermissions: true,
            stale: false,
            distanceFilter: NATIVE_DISTANCE_FILTER,
            backgroundTitle: 'ProPilote — Suivi actif',
            backgroundMessage: 'Votre position est transmise au répartiteur pendant votre quart de travail.',
          },
          (location, err) => {
            if (err) {
              console.error('[GPS Native] Erreur watcher:', err);
              if (err.code === 'NOT_AUTHORIZED') {
                setError('Permission de localisation refusée. Activez "Toujours" dans les Réglages iOS.');
                // Optionally open Settings
                BackgroundGeolocation.openSettings?.().catch(() => {});
              } else {
                setError(err.message || 'Erreur GPS');
              }
              return;
            }
            handlePosition(nativeToPosition(location));
          }
        );

        watchIdRef.current = { native: true, id: watcherId };
        setIsTracking(true);
        setError(null);

        // Connect to WebSocket
        connectWebSocket();
        // Start HTTP polling as fallback
        startHttpPolling();

        console.log('[GPS Native] Tracking natif démarré (watcherId:', watcherId + ')');
        return true;
      } catch (nativeErr) {
        console.error('[GPS Native] Échec, fallback vers navigator.geolocation:', nativeErr);
        setError(nativeErr.message || 'Erreur GPS natif');
        // Fall through to web fallback
      }
    }

    // Web mode (PWA / navigateur): use navigator.geolocation
    if (!navigator.geolocation) {
      setError('Géolocalisation non supportée par ce navigateur');
      return false;
    }

    // Request permission and start watching
    navigator.geolocation.getCurrentPosition(
      (position) => {
        handlePosition(position);
        setIsTracking(true);
        setError(null);

        // Connect to WebSocket
        connectWebSocket();

        // Start HTTP polling as fallback
        startHttpPolling();

        // Start watching position with high accuracy
        watchIdRef.current = navigator.geolocation.watchPosition(
          handlePosition,
          handleError,
          {
            enableHighAccuracy: true,
            timeout: 15000,
            maximumAge: 5000 // Use cached position up to 5 seconds old
          }
        );
        
        console.log('[GPS Web] Tracking démarré avec WebSocket + HTTP polling fallback');
      },
      (err) => {
        handleError(err);
        return false;
      },
      { enableHighAccuracy: true, timeout: 15000 }
    );

    return true;
  }, [handlePosition, handleError, connectWebSocket, startHttpPolling]);

  // Stop tracking
  const stopTracking = useCallback(async () => {
    // Désactiver SCLS côté natif en persistant le flag à false
    if (IS_NATIVE) {
      try {
        const { Preferences } = await import('@capacitor/preferences');
        await Preferences.set({ key: 'propilote_gps_enabled', value: 'false' });
        console.log('[GPS Native] SCLS désactivé (flag persisté)');
      } catch (e) { /* silent */ }
    }

    // Android: arreter le service natif Life360-style
    if (IS_ANDROID && PropilotGps) {
      try {
        await PropilotGps.stop();
        console.log('[GPS Android] PropilotGps native service arrete');
      } catch (e) {
        console.warn('[GPS Android] Erreur arret PropilotGps:', e);
      }
    }

    // Stop watching position
    if (watchIdRef.current !== null) {
      // Native watcher
      if (typeof watchIdRef.current === 'object' && watchIdRef.current.native) {
        try {
          await BackgroundGeolocation?.removeWatcher({ id: watchIdRef.current.id });
          console.log('[GPS Native] Watcher natif retiré');
        } catch (e) {
          console.error('[GPS Native] Erreur retrait watcher:', e);
        }
      } else {
        // Web watcher (numeric ID)
        navigator.geolocation.clearWatch(watchIdRef.current);
      }
      watchIdRef.current = null;
    }
    
    // Close WebSocket
    if (wsRef.current) {
      wsRef.current.close(1000, 'Tracking stopped');
      wsRef.current = null;
    }
    
    // Clear intervals and timeouts
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (httpPollIntervalRef.current) {
      clearInterval(httpPollIntervalRef.current);
      httpPollIntervalRef.current = null;
    }
    
    setIsTracking(false);
    setIsConnected(false);
    console.log('[GPS] Tracking arrêté');
  }, []);

  // Auto-start when authenticated AND the hook is enabled (driver routes only)
  useEffect(() => {
    if (enabled && isAuthenticated && !isTracking) {
      // Check if user has explicitly disabled location sharing
      const settings = localStorage.getItem('notificationSettings');
      let shouldTrack = true;

      if (settings) {
        try {
          const parsed = JSON.parse(settings);
          shouldTrack = parsed.locationTracking !== false;
        } catch (e) {}
      }

      if (shouldTrack) {
        // Slight delay to let auth context settle and avoid permission prompt
        // appearing before the user sees the screen.
        const t = setTimeout(() => startTracking(), 800);
        return () => clearTimeout(t);
      }
    }

    if (!enabled && isTracking) {
      stopTracking();
    }

    // Auto-stop du GPS à la déconnexion du compte (isAuthenticated passe à false)
    if (!isAuthenticated && isTracking) {
      console.log('[GPS] Déconnexion détectée — arrêt du suivi');
      stopTracking();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, isAuthenticated]);

  // Clean up on unmount only
  useEffect(() => {
    return () => stopTracking();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reconnect WebSocket if token changes
  useEffect(() => {
    if (isTracking && token && !isConnected) {
      connectWebSocket();
    }
  }, [token, isTracking, isConnected, connectWebSocket]);

  return {
    isTracking,
    isConnected, // New: indicates WebSocket connection status
    lastPosition,
    error,
    startTracking,
    stopTracking
  };
};

export default useLocationTracking;
